import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { CrownIcon } from "../../components/icons";
import { getProfileKind, PROFILE_KIND } from "../../utils/listSchema";
import { formatCount, getContentOptions } from "../../utils/utils";

// Display title + icon — the stored list titles predate the profile redesign
const HEADINGS = {
  [PROFILE_KIND.TOP5_ALLTIME]: { title: "All-time top 5", icon: <CrownIcon /> },
};

/**
 * "5 favourites from 120 watched" ("read", for books; "series watched" for TV,
 * whose finished count is of series, not seasons) — a pick collection's
 * description. Exported so a collapsed one can show it beside its empty state.
 */
export function getFavouritesDescription(list, finishedCount) {
  const itemCount = list.items.length;
  const verb =
    {
      book: "read",
      tvshow: "series watched",
    }[list.content_type] ?? "watched";
  // formatCount renders 0 as "—", but 0 is a real count here
  return (
    `${itemCount} ${itemCount === 1 ? "favourite" : "favourites"} from ` +
    `${finishedCount === 0 ? 0 : formatCount(finishedCount)} ${verb}`
  );
}

/**
 * Title shown in a profile pick collection's first slot, styled like the page's
 * section headers: h6 bold with a primary-coloured icon in front.
 *
 * `grouped`: several types' lists sit under one shared heading (the profile's
 * All view), so the list is titled by its content type instead ("Movies",
 * with that type's icon; TV shortened to "TV").
 *
 * With `finishedCount`, getFavouritesDescription's line sits underneath,
 * unless `hideDescription` (it's shown elsewhere).
 */
export default function ProfileCollectionMetadata({
  list,
  grouped,
  finishedCount,
  hideDescription = false,
}) {
  const typeOption = grouped ? getContentOptions()[list.content_type] : null;
  const heading = HEADINGS[getProfileKind(list)];
  const icon = typeOption?.icon ?? heading?.icon;
  const title = typeOption
    ? list.content_type === "tvshow"
      ? "TV"
      : typeOption.labelPlural
    : (heading?.title ?? list.title);

  const description =
    !hideDescription &&
    finishedCount != null &&
    getFavouritesDescription(list, finishedCount);

  return (
    <Box>
      {/* Top-aligned: in the narrow title slot the text can wrap to two lines.
          6px icon gap, matching the section headers (components/SectionHeader) */}
      <Stack direction="row" alignItems="flex-start" spacing={0.75}>
        {icon && React.cloneElement(icon, { color: "primary" })}
        <Typography variant="h6" fontWeight="bold" lineHeight={1.3}>
          {title}
        </Typography>
      </Stack>
      {description && (
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {description}
        </Typography>
      )}
    </Box>
  );
}
